import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Button,
  Slider,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Stack,
  Checkbox,
} from '@mui/material';
import { AccountBalance as AccountIcon, ChevronRight, ChevronLeft } from '@mui/icons-material';
import { CategorySelector } from '../categories/CategorySelector';
import { Category } from '../../types';
import { useListPlaidAccounts } from '../../hooks/useListPlaidAccounts';
import { useDisbursementSettings } from '../../hooks/useDisbursementSettings';
import { useCreateDisbursementRule } from '../../hooks/useCreateDisbursementRule';

interface DisbursementRuleFormProps {
  onSubmit: (category: Category, accounts: { accountId: string; percentage: number }[]) => void;
  onCancel: () => void;
  initialCategory?: Category;
  initialAccounts?: { accountId: string; percentage: number }[];
}

const DisbursementRuleForm: React.FC<DisbursementRuleFormProps> = ({
  onSubmit,
  onCancel,
  initialCategory,
  initialAccounts,
}) => {
  const { data: plaidAccounts, isLoading, isError } = useListPlaidAccounts();
  const { data: disbursementSettings } = useDisbursementSettings();
  const createRule = useCreateDisbursementRule();
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(initialCategory);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(
    initialAccounts ? initialAccounts.map(a => a.accountId) : []
  );
  const [percentage, setPercentage] = useState<number>(100);
  const [checked, setChecked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter out the source account from selectable accounts
  const filteredAccounts = useMemo(() => {
    if (!plaidAccounts) return [];
    const sourceId = disbursementSettings?.sourceAccount?.id;
    return plaidAccounts.filter((acc: any) => acc.id !== sourceId);
  }, [plaidAccounts, disbursementSettings]);

  const availableAccounts = filteredAccounts.filter(acc => !selectedAccountIds.includes(String(acc.id)));
  const selectedAccounts = filteredAccounts.filter(acc => selectedAccountIds.includes(String(acc.id)));

  const handleToggle = (accountId: string) => {
    setChecked(prev =>
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    );
  };

  const handleAdd = () => {
    setSelectedAccountIds(prev => [...prev, ...checked.filter(id => !prev.includes(id))]);
    setChecked([]);
  };

  const handleRemove = () => {
    setSelectedAccountIds(prev => prev.filter(id => !checked.includes(id)));
    setChecked([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (selectedCategory && selectedAccountIds.length > 0) {
      try {
        await createRule.mutateAsync({
          category: selectedCategory.id,
          percentage: percentage / 100,
          destination_account_ids: selectedAccountIds.map(Number),
        });
        setSelectedCategory(undefined);
        setSelectedAccountIds([]);
        setPercentage(100);
        onSubmit && onSubmit(
          selectedCategory,
          selectedAccountIds.map(accountId => ({ accountId, percentage }))
        );
      } catch (err: any) {
        setError(err.message || 'Failed to create rule');
      }
    }
  };

  // Debug logging for selectedCategory
  React.useEffect(() => {
    console.log('selectedCategory', selectedCategory);
  }, [selectedCategory]);

  return (
    <form onSubmit={handleSubmit}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 2, background: 'rgba(255,255,255,0.95)', maxWidth: 700, mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Create New Rule
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CategorySelector
              onSelect={setSelectedCategory}
              selectedCategory={selectedCategory}
              label="Spending Category"
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountIcon /> Transfer To Accounts
              </Typography>
              {isLoading ? (
                <Typography>Loading accounts...</Typography>
              ) : isError ? (
                <Typography color="error">Failed to load accounts.</Typography>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                  {/* Available Accounts List */}
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Available</Typography>
                    <List dense sx={{ border: '1px solid #eee', borderRadius: 1, minHeight: 120, maxHeight: 200, overflow: 'auto' }}>
                      {availableAccounts.map((account: any) => (
                        <ListItem
                          key={account.id}
                          button
                          selected={checked.includes(String(account.id))}
                          onClick={() => handleToggle(String(account.id))}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={checked.includes(String(account.id))}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ 'aria-labelledby': `available-account-${account.id}` }}
                            />
                          </ListItemIcon>
                          <ListItemText id={`available-account-${account.id}`} primary={account.name} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  {/* Transfer Buttons */}
                  <Stack direction="column" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: { xs: 1, sm: 6 } }}>
                    <IconButton onClick={handleAdd} disabled={checked.filter(id => availableAccounts.some(acc => String(acc.id) === id)).length === 0}>
                      <ChevronRight />
                    </IconButton>
                    <IconButton onClick={handleRemove} disabled={checked.filter(id => selectedAccountIds.includes(id)).length === 0}>
                      <ChevronLeft />
                    </IconButton>
                  </Stack>
                  {/* Selected Accounts List */}
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Selected</Typography>
                    <List dense sx={{ border: '1px solid #eee', borderRadius: 1, minHeight: 120, maxHeight: 200, overflow: 'auto' }}>
                      {selectedAccounts.map((account: any) => (
                        <ListItem
                          key={account.id}
                          button
                          selected={checked.includes(String(account.id))}
                          onClick={() => handleToggle(String(account.id))}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={checked.includes(String(account.id))}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ 'aria-labelledby': `selected-account-${account.id}` }}
                            />
                          </ListItemIcon>
                          <ListItemText id={`selected-account-${account.id}`} primary={account.name} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Stack>
              )}
              <Box sx={{ mt: 2, width: '100%' }}>
                <Typography gutterBottom>Percentage</Typography>
                <Slider
                  value={percentage}
                  min={1}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, value) => setPercentage(Number(value))}
                  sx={{ width: '100%' }}
                />
                <Typography sx={{ minWidth: 40 }}>{percentage}%</Typography>
              </Box>
            </Box>
          </Grid>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!selectedCategory || selectedAccountIds.length === 0 || createRule.isPending}
              sx={{ mt: 2, borderRadius: 2, fontWeight: 500 }}
              fullWidth
            >
              {createRule.isPending ? 'Creating...' : 'Create Rule'}
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{ mt: 2, borderRadius: 2, fontWeight: 500 }}
              fullWidth
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
};

export default DisbursementRuleForm; 